import { useSessionUser } from "hooks/useSession";
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Input, TextInput } from "components/common/input";
import FormGroupLabel from "components/common/text/FormGroupLabel";
import { SubscriptionType } from "store/types/enum";
import { useBudgetApi, useBudgetMutations } from "hooks/useBudget";
import { useStripboardApi } from "hooks/useStripboard";
import { useProjectIdentifiers } from "store/hooks/useProjectIdentifiers";
import { Checkbox } from "components/common";
import { Controller, FormProvider, useForm } from "react-hook-form";
import {
  BudgetAssumptions,
  Currency,
  TimeUnit,
  createBudgetAssumptionsSchema,
} from "./BudgetAssumptionsTypes";
import { yupResolver } from "@hookform/resolvers/yup";
import { BudgetCategory, BudgetI, BudgetSubCategory } from "./types";
import { useSelector } from "react-redux";
import { AppState } from "store/reducers/Reducer";
import { MdLock } from "react-icons/md";
import {
  getAllItemsInCategories,
  getAllItemsInSubCategories,
  getBudgetItemsEstimate,
  getBudgetItemsEstimateIncludingFringes,
} from "./BudgetItemUtils";
import { AiOutlineWarning } from "react-icons/ai";
import { ProductionBaseField } from "components/common/ProductionBaseField";
import { TaxIncentivesModal } from "./TaxIncentivesModal";
import { ProductionState } from "config";
import { US_STATES } from "config/states";
import { useVersionSettings } from "hooks/useProjects/useVersions";
import {
  createDefaultBudgetAssumptionsFormValues,
  formValuesToBudgetAssumptions,
  parseVersionProductionLocation,
} from "utils/budgetAssumptionsMapping";
import TimeUnitSelector from "./TimeUnitSelector";
import TooltipV2 from "components/common/TooltipV2/TooltipV2";
import { Switch } from "components/common/switch";
import { NumericFormat } from "react-number-format";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "components/common/modal";
import { Button } from "components/common/buttons/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "components/common/dropdown/DropDownV2";
import { LuChevronsUpDown } from "react-icons/lu";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/common/tooltips/Tooltip";
import { Stars } from "lucide-react";
import { instanceOfStripboardTableDayBreak } from "utils";
import { useBudgetRunActive } from "hooks/budget/useBudgetRunActive";
import { useAlertDialog } from "context/AlertDialogContext";
import { useProjectsApi } from "hooks/useProjects";
import { useProjectAuth } from "hooks/project/useProjectAuth";
import { useCreateProjectPermissionGuard } from "components/common/CreateProjectPermissionGuard";
import { PurchaseProjectModal } from "components/common/modals/PurchaseProjectModal";
import { getBudgetRerunAvailability } from "utils/aiCreditsAvailability";
import {
  areScheduleBudgetRunPrerequisitesMet,
  getScheduleBudgetRunPrerequisiteMessage,
} from "utils/scheduleBudgetRunPrerequisites";
import { handleToast } from "utils/toasts";

interface EditBudgetAssumptionsModalProps {
  open: boolean;
  setOpen: () => void;
  lockedCategories: BudgetSubCategory[];
  sheetData: BudgetCategory[];
  budgetDetail?: Omit<BudgetI, "categories">;
  handleUpdateBudgetCurrency: (currency: string) => void;
  scriptHasMinimalRunContent?: boolean;
  budgetMergePayload?: { merge_policy?: string };
  /** Fires immediately before budget rerun mutation (no assumption payload). */
  onPipelineRunRequested?: () => void;
}

const STRIPBOARD_DAY_BREAK_TOOLTIP = "Add a shoot day to the stripboard first";
const BUDGET_RUN_IN_PROGRESS_NOTE_ID = "budget-run-in-progress-note";

type TBudgetAssumptionsSchema = Omit<BudgetAssumptions, "max_budget"> & {
  max_budget: { currency: Currency.USD; value: string };
};

export const EditBudgetAssumptionsModal: React.FC<EditBudgetAssumptionsModalProps> = ({
  open,
  setOpen,
  lockedCategories: lockedSubCategories,
  sheetData,
  budgetDetail,
  budgetMergePayload,
  onPipelineRunRequested,
}) => {
  const [licensePurchaseModalOpen, setLicensePurchaseModalOpen] = useState(false);
  const { showAlertDialog } = useAlertDialog();

  const { projectId, documentId } = useProjectIdentifiers();
  const { useProject } = useProjectsApi({});
  const { data: projectFromQuery } = useProject(projectId ? String(projectId) : undefined);
  const { embeddedProjectLicense } = useProjectAuth(projectId ? String(projectId) : undefined);
  const isUnlicensedProject = embeddedProjectLicense.isLicensed === false;
  const createProjectGuard = useCreateProjectPermissionGuard(projectFromQuery ?? undefined);

  const onBudgetRerunBlockedByLicense = useCallback(() => {
    setLicensePurchaseModalOpen(true);
  }, []);

  const project = useSelector((state: AppState) => state.project);
  const { session: profile, isOneTimePurchase } = useSessionUser();
  const pricing_plan = profile?.pricing_plan;
  const isFreePlan = pricing_plan?.plan_name === SubscriptionType.FREE;
  const isStudioPlan = pricing_plan?.plan_name === SubscriptionType.STUDIO;

  const isTrialProject = project.is_trial;

  const { budgetId } = useBudgetApi(true);
  const { reRunBudgeting, saveBudgetAssumptions } = useBudgetMutations({
    projectId,
    documentId,
    budgetId,
    budgetRerunGuard: isUnlicensedProject
      ? { blocked: true, onBlocked: onBudgetRerunBlockedByLicense }
      : undefined,
  });

  const { stripboardData } = useStripboardApi(false, open);
  const versionSettingsQuery = useVersionSettings(projectId, documentId ?? undefined);
  const [taxIncentiveState, setTaxIncentiveState] = useState<ProductionState>();
  const hydratedBudgetIdRef = useRef<number | null>(null);
  const hasLoadedStripboardData = stripboardData !== undefined;
  const hasLastDayBreak = useMemo(() => {
    if (!stripboardData?.length) return false;

    const lastItem = stripboardData[stripboardData.length - 1];
    return instanceOfStripboardTableDayBreak(lastItem) && Boolean(lastItem.daybreak_id);
  }, [stripboardData]);
  // Same rule as the API budget rerun guard: the stripboard toggles need at least one day break.
  const hasDayBreaks = useMemo(
    () =>
      (stripboardData ?? []).some(
        (row) => instanceOfStripboardTableDayBreak(row) && Boolean(row.daybreak_id)
      ),
    [stripboardData]
  );
  const showStripboardPrerequisiteWarning = hasLoadedStripboardData && !hasDayBreaks;

  const getTotalBudget = () => {
    if (!budgetDetail) return 0;
    const allItemsInCategories = getAllItemsInCategories(sheetData);

    const budgetTotalEstimateIncludingFringes =
      getBudgetItemsEstimateIncludingFringes(allItemsInCategories);

    const contingencyEstimate =
      (budgetDetail.contingency_percentage / 100) * budgetTotalEstimateIncludingFringes;
    return budgetTotalEstimateIncludingFringes + contingencyEstimate;
  };
  const BudgetAssumptionsSchema = createBudgetAssumptionsSchema(
    getBudgetItemsEstimate(getAllItemsInSubCategories(lockedSubCategories))
  );

  const methods = useForm<TBudgetAssumptionsSchema>({
    resolver: yupResolver(BudgetAssumptionsSchema),
    defaultValues: createDefaultBudgetAssumptionsFormValues({
      lockedAccountsTotal: getBudgetItemsEstimate(getAllItemsInSubCategories(lockedSubCategories)),
      currentGridTotal: getTotalBudget(),
    }),
  });

  const {
    clearErrors,
    getValues,
    handleSubmit,
    reset,
    resetField,
    setValue,
    watch,
    control,
    formState: { errors },
  } = methods;

  const versionProductionLocation = useMemo(() => {
    const data = versionSettingsQuery.data;
    if (!data || typeof data !== "object") return null;
    const record = data as Record<string, unknown>;
    return parseVersionProductionLocation(record.production_location);
  }, [versionSettingsQuery.data]);

  const versionTaxState = useMemo((): ProductionState | null => {
    const data = versionSettingsQuery.data;
    if (!data || typeof data !== "object") return null;
    const record = data as Record<string, unknown>;
    const raw =
      typeof record.production_state === "string"
        ? record.production_state
        : typeof project.features?.production_state === "string"
          ? project.features.production_state
          : null;
    if (!raw) return null;
    if (Object.values(ProductionState).includes(raw as ProductionState)) {
      return raw as ProductionState;
    }
    const byCode = US_STATES.find((state) => state.value === raw || state.label === raw);
    return byCode ? (byCode.label as ProductionState) : null;
  }, [project.features?.production_state, versionSettingsQuery.data]);

  const hasSavedAssumptions = budgetDetail?.rerun_params != null;

  useEffect(() => {
    if (!open) {
      hydratedBudgetIdRef.current = null;
      return;
    }
    const currentBudgetId = budgetDetail?.budget_id ?? null;
    if (currentBudgetId != null && hydratedBudgetIdRef.current === currentBudgetId) {
      return;
    }

    const seededValues = createDefaultBudgetAssumptionsFormValues({
      lockedAccountsTotal: getBudgetItemsEstimate(getAllItemsInSubCategories(lockedSubCategories)),
      currentGridTotal: getTotalBudget(),
      rerunParams: budgetDetail?.rerun_params ?? null,
      versionProductionLocation,
    });

    // When the stripboard is fully scheduled and nothing is saved yet, default the
    // stripboard-driven toggles on. Saved assumptions always win. Seed that here (not only in
    // the post-mount effect below) so the dialog opens directly in its correct state instead of
    // rendering toggles-off first and then shifting layout.
    if (hasLastDayBreak && !hasSavedAssumptions) {
      seededValues.use_stripboard_shoot_time = true;
      seededValues.use_stripboard_locations = true;
      seededValues.shoot = null;
      seededValues.location = undefined;
    }

    reset(seededValues);
    hydratedBudgetIdRef.current = currentBudgetId;
  }, [
    open,
    budgetDetail?.budget_id,
    budgetDetail?.rerun_params,
    hasLastDayBreak,
    hasSavedAssumptions,
    lockedSubCategories,
    reset,
    versionProductionLocation,
  ]);

  useEffect(() => {
    // Covers a stripboard that finishes loading after the dialog opened. Same rule as the
    // seeding above: the default never overrides saved assumptions.
    if (!hasLastDayBreak || hasSavedAssumptions) return;

    methods.setValue("use_stripboard_shoot_time", true);
    methods.setValue("shoot", null);
    methods.setValue("use_stripboard_locations", true);
    methods.resetField("location");
  }, [hasLastDayBreak, hasSavedAssumptions, methods]);

  const budgetAvail = getBudgetRerunAvailability(projectFromQuery ?? undefined);
  const budgetRerunsLeft = budgetAvail.remainingUnits;
  const rerunsExhausted = budgetAvail.exhausted;

  const canRunAiRebudget = areScheduleBudgetRunPrerequisitesMet(projectFromQuery ?? undefined);
  const budgetPrerequisiteMessage = getScheduleBudgetRunPrerequisiteMessage(
    projectFromQuery ?? undefined
  );

  // Same rule the re-run guard in useBudgetMutations applies before sending.
  const isBudgetRunActive = useBudgetRunActive(projectId, documentId);

  const handleConfirm = () => {
    if (!canRunAiRebudget) {
      handleToast(
        budgetPrerequisiteMessage ??
          "Finish script breakdown before running schedule or budget AI.",
        "error"
      );
      return;
    }
    if (!isOneTimePurchase && isFreePlan) return;
    if (isTrialProject) return;

    const budgetAssumptions = formValuesToBudgetAssumptions(getValues());

    onPipelineRunRequested?.();

    reRunBudgeting.mutate(
      { ...budgetAssumptions, ...(budgetMergePayload ?? {}) },
      {
        onSuccess: () => {
          setOpen();
        },
      }
    );
  };

  const handleSaveAssumptions = handleSubmit((values) => {
    const budgetAssumptions = formValuesToBudgetAssumptions(values);
    saveBudgetAssumptions.mutate(budgetAssumptions, {
      onSuccess: () => {
        handleToast("Budget assumptions saved.", "success");
      },
    });
  });

  const handleValidSubmit = () => {
    if (isBudgetRunActive) return;
    showAlertDialog({
      title: "Overwrite current budget?",
      type: "warning",
      message:
        "AI rebudget replaces line items from the previous run with new estimates. Locked accounts and categories stay unchanged.",
      actions: {
        cancel: "Cancel",
        success: {
          text: "Run AI rebudget",
          onSuccess: handleConfirm,
        },
      },
    });
  };

  const subscriptionFooterText = !isOneTimePurchase
    ? isFreePlan
      ? "Upgrade to access this feature."
      : rerunsExhausted
        ? `No AI credits left for rebudget (${budgetAvail.cost} credits per run).${
            !isStudioPlan ? " Upgrade for more." : ""
          }`
        : !isStudioPlan
          ? `AI credits: ${budgetRerunsLeft} remaining (${budgetAvail.cost} per rebudget). Upgrade for more capacity.`
          : null
    : null;

  const GuildsCheckbox = ({
    name,
    label,
  }: {
    name: "guilds.dga" | "guilds.iatse" | "guilds.sag" | "guilds.teamsters" | "guilds.wga";
    label: string;
  }) => (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value } }) => (
        <div className="flex gap-2">
          <Checkbox name="checkbox" checked={Boolean(value)} onChange={() => onChange(!value)} />
          <span className="text-regular-3">{label}</span>
        </div>
      )}
    />
  );

  return (
    <>
      <Modal isOpen={open} onClose={setOpen}>
        <ModalHeader
          title={"Budget assumptions"}
          subtitle="Update production base, timeline, and guild settings. Save without rerunning AI, or run AI rebudget to regenerate line items."
          onClose={setOpen}
        />
        <ModalBody>
          <div className="flex flex-col gap-6 pb-4">
            {budgetPrerequisiteMessage ? (
              <p
                className="text-xs text-primary flex gap-1.5 items-start"
                role="status"
                aria-live="polite"
              >
                <AiOutlineWarning className="shrink-0 mt-0.5" aria-hidden />
                <span>{budgetPrerequisiteMessage}</span>
              </p>
            ) : null}
            {budgetDetail?.rerun_params_notice ? (
              <p className="text-xs text-muted-foreground" role="note">
                {budgetDetail.rerun_params_notice}
              </p>
            ) : null}
            <FormProvider {...methods}>
              <form
                onSubmit={handleSubmit(handleValidSubmit)}
                id="edit-budget-assumptions-form"
                className="flex flex-col gap-6"
              >
                {showStripboardPrerequisiteWarning && (
                  <p
                    className="text-xs text-primary flex gap-1.5 items-start -mt-2"
                    role="status"
                    aria-live="polite"
                  >
                    <AiOutlineWarning className="shrink-0 mt-0.5" aria-hidden />
                    <span>
                      Add at least one shoot day to the stripboard before you can use stripboard
                      shoot time or stripboard locations.
                    </span>
                  </p>
                )}
                <DropdownMenu modal={false}>
                  <Tooltip placement="right">
                    <TooltipTrigger asChild>
                      <DropdownMenuTrigger className="w-fit">
                        <div className="flex flex-row items-center gap-2 uppercase">
                          <MdLock size={15} />
                          <span>{lockedSubCategories.length ? `See` : `No`} locked accounts</span>
                          <LuChevronsUpDown />
                        </div>
                      </DropdownMenuTrigger>
                    </TooltipTrigger>
                    <TooltipContent>Locked accounts will remain unchanged</TooltipContent>
                  </Tooltip>
                  {lockedSubCategories.length > 0 && (
                    <DropdownMenuContent className="max-h-64 overflow-y-auto">
                      {lockedSubCategories.map((category) => (
                        <DropdownMenuItem key={category.name}>{category.name}</DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  )}
                </DropdownMenu>
                <div>
                  <div className="flex gap-3">
                    <div className="flex flex-1 gap-2 items-center">
                      <div className="w-full relative">
                        <NumericFormat
                          label="Max Budget"
                          name="max_budget.value"
                          id="max_budget"
                          value={watch("max_budget.value")}
                          onValueChange={(values) => {
                            setValue("max_budget.value", values.formattedValue ?? 0);
                          }}
                          thousandSeparator
                          allowNegative={false}
                          decimalScale={0}
                          customInput={Input}
                          inputSelector={
                            <span className="absolute right-2 top-2.5 mr-2 mt-[2px] flex flex-col text-sm text-foreground">
                              {budgetDetail?.currency.toUpperCase()}
                            </span>
                          }
                          size="sm"
                          opaqueOnHover={false}
                          required
                        />
                        {budgetDetail?.currency !== Currency.USD && (
                          <span className="text-xs text-danger pl-3 opacity-60">
                            Your budget is in {budgetDetail?.currency.toUpperCase()}. Please convert
                            it to {Currency.USD} to rerun the budget.
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex justify-between">
                  <span>
                    <span className="text-regular-2 opacity-60 uppercase">Timeline</span>
                    <span className="text-primary"> *</span>
                  </span>
                  <div className="flex items-center gap-2">
                    <TooltipV2
                      content={!hasDayBreaks ? STRIPBOARD_DAY_BREAK_TOOLTIP : ""}
                      delayDuration={0}
                    >
                      <div className="flex items-center gap-2">
                        <Controller
                          name="use_stripboard_shoot_time"
                          control={control}
                          render={({ field }) => (
                            <Switch
                              checked={field.value}
                              // Saved assumptions can have this on while the board has no
                              // day breaks. Keep it switchable off so the user can enter a
                              // manual shoot length; only turning it on needs day breaks.
                              disabled={!hasDayBreaks && !field.value}
                              onChange={(checked) => {
                                if (checked && !hasDayBreaks) return;
                                field.onChange(checked);
                                if (checked) {
                                  // The Shoot field is hidden in this mode, so it must not
                                  // hold a half-filled value that blocks Save and AI rebudget.
                                  setValue("shoot", null);
                                  clearErrors("shoot");
                                } else {
                                  // Bring back the saved manual length, or an empty one in weeks.
                                  resetField("shoot");
                                  if (!getValues("shoot")) {
                                    setValue("shoot.unit", TimeUnit.WEEK);
                                  }
                                }
                              }}
                            />
                          )}
                        />
                        <span className="text-regular-2 opacity-60">Use Stripboard Shoot Time</span>
                      </div>
                    </TooltipV2>
                  </div>
                </div>
                <div className="flex gap-3 mb-4">
                  <Input
                    label={"Prep"}
                    name="prep.value"
                    type="number"
                    size="sm"
                    compactErrorMessage
                    opaqueOnHover={false}
                    inputSelector={
                      <Controller
                        name="prep.unit"
                        control={control}
                        render={({ field }) => (
                          <TimeUnitSelector
                            value={field.value}
                            onChange={(timeUnit: TimeUnit) => field.onChange(timeUnit)}
                          />
                        )}
                      />
                    }
                  />
                  {!watch("use_stripboard_shoot_time") ? (
                    <div className="flex flex-col w-full">
                      <Input
                        label={"Shoot"}
                        name="shoot.value"
                        type="number"
                        size="sm"
                        compactErrorMessage
                        opaqueOnHover={false}
                        inputSelector={
                          <Controller
                            name="shoot.unit"
                            control={control}
                            render={({ field }) => (
                              <TimeUnitSelector
                                value={field.value}
                                onChange={(timeUnit: TimeUnit) => field.onChange(timeUnit)}
                              />
                            )}
                          />
                        }
                      />
                    </div>
                  ) : (
                    <TextInput
                      disabled
                      label={"Shoot"}
                      textClass="font-normal"
                      name={"Shoot"}
                      type={"text"}
                      value={"auto"}
                      size="px-4 py-2.5 min-h-[42px]"
                      background="bg-primary/10"
                      handleChange={() => {}}
                    />
                  )}
                  <Input
                    label={"Wrap"}
                    name="wrap.value"
                    type="number"
                    size="sm"
                    compactErrorMessage
                    opaqueOnHover={false}
                    inputSelector={
                      <Controller
                        name="wrap.unit"
                        control={control}
                        render={({ field }) => (
                          <TimeUnitSelector
                            value={field.value}
                            onChange={(timeUnit: TimeUnit) => field.onChange(timeUnit)}
                          />
                        )}
                      />
                    }
                  />
                  <Input
                    label={"Post"}
                    name="post.value"
                    type="number"
                    size="sm"
                    compactErrorMessage
                    opaqueOnHover={false}
                    inputSelector={
                      <Controller
                        name="post.unit"
                        control={control}
                        render={({ field }) => (
                          <TimeUnitSelector
                            value={field.value}
                            onChange={(timeUnit: TimeUnit) => field.onChange(timeUnit)}
                          />
                        )}
                      />
                    }
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <div className="flex justify-between">
                    <span>
                      <span className="text-regular-2 opacity-60 uppercase">Production base</span>
                      <span className="text-primary"> *</span>
                    </span>
                    <div className="flex items-center gap-2">
                      <TooltipV2
                        content={!hasDayBreaks ? STRIPBOARD_DAY_BREAK_TOOLTIP : ""}
                        delayDuration={0}
                      >
                        <div className="flex items-center gap-2">
                          <Controller
                            name="use_stripboard_locations"
                            control={control}
                            render={({ field }) => (
                              <Switch
                                checked={field.value}
                                // Same as shoot time: always switchable off, so the user can
                                // set a production base; turning it on needs day breaks.
                                disabled={!hasDayBreaks && !field.value}
                                onChange={(checked) => {
                                  if (checked && !hasDayBreaks) return;
                                  field.onChange(checked);
                                  if (checked) {
                                    resetField("location");
                                  }
                                }}
                              />
                            )}
                          />
                          <span className="text-regular-2 opacity-60">
                            Use stripboard scene locations
                          </span>
                        </div>
                      </TooltipV2>
                    </div>
                  </div>
                  {!watch("use_stripboard_locations") ? (
                    <Controller
                      name="location"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <ProductionBaseField
                          value={{
                            name: value?.name ?? "",
                            place_id: value?.place_id ?? null,
                          }}
                          onChange={onChange}
                          taxState={versionTaxState}
                          onViewTaxIncentives={(state) => setTaxIncentiveState(state)}
                          showLabel={false}
                          helperText="Set your production base to align budget, tax incentives, and AI assumptions."
                        />
                      )}
                    />
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      Scene locations from the stripboard will be sent to the AI worker instead of a
                      single production base.
                    </p>
                  )}
                  {errors.location && !watch("use_stripboard_locations") ? (
                    <div className="pr-0 text-primary text-regular-3 flex gap-1 items-center">
                      <AiOutlineWarning />
                      {errors.location?.message?.toString()}
                    </div>
                  ) : null}
                </div>
                <div>
                  <FormGroupLabel marginBottom>GUILDS:</FormGroupLabel>

                  <div className="flex gap-3">
                    <GuildsCheckbox name="guilds.wga" label="WGA" />
                    <GuildsCheckbox name="guilds.dga" label="DGA" />
                    <GuildsCheckbox name="guilds.sag" label="SAG-AFTRA" />
                    <GuildsCheckbox name="guilds.iatse" label="IATSE" />
                    <GuildsCheckbox name="guilds.teamsters" label="TEAMSTERS" />
                  </div>
                </div>
              </form>
            </FormProvider>
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="flex w-full flex-col gap-2">
            <div className="flex w-full items-center justify-end gap-2">
              <Button size="lg" variant="ghost" onClick={setOpen}>
                Close
              </Button>
              <Button
                type="button"
                size="lg"
                variant="subtle"
                isLoading={saveBudgetAssumptions.isPending}
                disabled={saveBudgetAssumptions.isPending || reRunBudgeting.isLoading}
                onClick={() => {
                  void handleSaveAssumptions();
                }}
              >
                Save assumptions
              </Button>
              <Tooltip>
                <TooltipTrigger>
                  <div className="inline-flex">
                    <Button
                      type="submit"
                      size="lg"
                      variant="ghostBrand"
                      isLoading={reRunBudgeting.isLoading}
                      aria-describedby={
                        isBudgetRunActive ? BUDGET_RUN_IN_PROGRESS_NOTE_ID : undefined
                      }
                      disabled={
                        isBudgetRunActive ||
                        !canRunAiRebudget ||
                        budgetDetail?.currency !== Currency.USD ||
                        isUnlicensedProject ||
                        (!isOneTimePurchase && (isFreePlan || rerunsExhausted)) ||
                        (isOneTimePurchase && isTrialProject)
                      }
                      form="edit-budget-assumptions-form"
                    >
                      {rerunsExhausted && !isOneTimePurchase ? (
                        <MdLock />
                      ) : isUnlicensedProject ? (
                        <MdLock />
                      ) : (
                        <Stars className="w-5 h-5" />
                      )}
                      AI Rebudget
                    </Button>
                  </div>
                </TooltipTrigger>
                {isTrialProject && (
                  <TooltipContent>
                    Rebudgeting is not available for trial projects. Purchase a license to unlock
                    this feature.
                  </TooltipContent>
                )}
                {isUnlicensedProject && !isTrialProject && (
                  <TooltipContent>Purchase a project license to run AI rebudget.</TooltipContent>
                )}
                {!isTrialProject &&
                  !isUnlicensedProject &&
                  budgetDetail?.currency !== Currency.USD && (
                    <TooltipContent>
                      Your budget is in {budgetDetail?.currency.toUpperCase()}. Please convert it to{" "}
                      {Currency.USD} to rerun the budget.
                    </TooltipContent>
                  )}
                {!isTrialProject &&
                  !isUnlicensedProject &&
                  budgetPrerequisiteMessage &&
                  budgetDetail?.currency === Currency.USD && (
                    <TooltipContent>{budgetPrerequisiteMessage}</TooltipContent>
                  )}
              </Tooltip>
            </div>
            {isBudgetRunActive ? (
              <p
                id={BUDGET_RUN_IN_PROGRESS_NOTE_ID}
                className="text-xs text-muted-foreground text-right"
                role="status"
                aria-live="polite"
              >
                A budget run is in progress. AI rebudget is available again when it finishes.
              </p>
            ) : null}
            {subscriptionFooterText !== null && (
              <div>
                <FormGroupLabel
                  className="mt-1"
                  noUpperCase
                  warningLabel={rerunsExhausted || isFreePlan}
                >
                  {subscriptionFooterText}
                </FormGroupLabel>
              </div>
            )}
            {isOneTimePurchase && isTrialProject && (
              <div>
                <FormGroupLabel className="mt-1" noUpperCase warningLabel={true}>
                  Rebudgeting is not available for trial projects. Purchase a license to unlock this
                  feature.
                </FormGroupLabel>
              </div>
            )}
          </div>
        </ModalFooter>
      </Modal>
      {taxIncentiveState ? (
        <TaxIncentivesModal
          open={Boolean(taxIncentiveState)}
          setOpen={() => setTaxIncentiveState(undefined)}
          productionState={taxIncentiveState}
        />
      ) : null}
      <PurchaseProjectModal
        open={licensePurchaseModalOpen}
        onClose={() => setLicensePurchaseModalOpen(false)}
        mode={createProjectGuard.permissions.purchaseModalMode}
        projectId={String(projectId)}
      />
    </>
  );
};

export default EditBudgetAssumptionsModal;
